import React, { useState, useEffect, useRef } from 'react';
import '../styles/ApplicationForm.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { useAnalyticsTracker } from '../hooks/useAnalyticsTracker';
import AnalyticsForm from './AnalyticsForm';

const ApplicationForm = () => {
  const analytics = useAnalyticsTracker('ApplicationForm');

  console.log('RECAPTCHA key:', process.env.REACT_APP_RECAPTCHA_SITE_KEY);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    causeName: '',
    causeURL: '',
    locationCity: '',
    locationState: '',
    missionStatement: '',
    visionGoals: '',
    fundraisingPlans: [],
    grantInterest: '',
    grantDetails: [],
    charitableCategory: '',
    fundsUsage: [],
    budgetDetails: [],
    budgetFile: null,
    applicantType: '',
    individualAddress: '',
    entityEIN: '',
    entityDocs: null,
    revenueYearOne: 0,
    revenueYearTwo: 0,
    depositAgreement: false,
    previousFiscalSponsor: '',
    fundsToTransfer: '',
    transferAmount: '',
    heardAboutUs: '',
    referralSource: '',
    otherSource: '',
    linkedin: '',
    instagram: '',
    facebook: '',
    otherSocials: '',
    comments: '',
    
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  useEffect(() => {
    if (!formData.budgetDetails.length) {
      setFormData({
        ...formData,
        budgetDetails: [
          { category: 'Programmatic Supplies', description: '', amount: '' },
          { category: 'Contractor Payments', description: '', amount: '' },
          { category: 'Vendor Payments', description: '', amount: '' },
          { category: 'Scholarships', description: '', amount: '' },
          { category: 'Travel', description: '', amount: '' },
          { category: 'Marketing', description: '', amount: '' },
          { category: 'Administrative Costs', description: '', amount: '' },
          { category: 'Other', description: '', amount: '' },
        ],
      });
    }
  }, []);
  
  const handleSliderChange = (e, field) => {
    const sliderValue = Number(e.target.value);
    // Non-linear mapping: 0-100 maps to 0-100k, 100-200 maps to 100k-10M
    let value;
    if (sliderValue <= 100) {
      // Linear mapping from 0-100k
      value = sliderValue * 1000;
    } else {
      // Exponential mapping from 100k-10M
      const percentage = (sliderValue - 100) / 100;
      value = 100000 + (percentage * percentage * 9900000);
    }
    
    setFormData({
      ...formData,
      [field]: Math.round(value)
    });
  };

  // Reverse mapping for slider value
  const getSliderValue = (value) => {
    if (value <= 100000) {
      return value / 1000;
    } else {
      const percentage = Math.sqrt((value - 100000) / 9900000);
      return 100 + (percentage * 100);
    }
  };

  // Update revenue slider ranges
  const revenueSliderRanges = ['$0', '$25k', '$50k', '$100k', '$1M', '$5M', '$10M'];

  const handleFileUpload = (e) => {
    const { name, files } = e.target;
    setFormData({
      ...formData,
      [name]: files[0],
    });
  };

  const toggleFundraisingOption = (option) => {
    const updatedOptions = formData.fundraisingPlans.includes(option)
      ? formData.fundraisingPlans.filter((item) => item !== option)
      : [...formData.fundraisingPlans, option];
    setFormData({ ...formData, fundraisingPlans: updatedOptions });
  };

  const handleGrantDetailsChange = (index, field, value) => {
    const updatedDetails = [...formData.grantDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };
    setFormData({ ...formData, grantDetails: updatedDetails });
  };

  const addGrantDetail = () => {
    setFormData({
      ...formData,
      grantDetails: [
        ...formData.grantDetails,
        { funder: '', amount: '', restrictions: '', rfp: null },
      ],
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0';
    return `$${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10); // Allow only 10 digits
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;

    return `${match[1] ? `(${match[1]}` : ''}${
      match[2] ? `) ${match[2]}` : ''
    }${match[3] ? `-${match[3]}` : ''}`;
  };

  const formatDollarAmount = (value) => {
    if (!value) return '';
    // Remove any non-digit characters
    const number = value.toString().replace(/[^\d.]/g, '');
    // Format with dollar sign and commas
    return `$${Number(number).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    })}`;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      causeName: '',
      causeURL: '',
      locationCity: '',
      locationState: '',
      missionStatement: '',
      visionGoals: '',
      fundraisingPlans: [],
      grantInterest: '',
      grantDetails: [],
      charitableCategory: '',
      fundsUsage: [],
      budgetDetails: [
        { category: 'Programmatic Supplies', description: '', amount: '' },
        { category: 'Contractor Payments', description: '', amount: '' },
        { category: 'Vendor Payments', description: '', amount: '' },
        { category: 'Scholarships', description: '', amount: '' },
        { category: 'Travel', description: '', amount: '' },
        { category: 'Marketing', description: '', amount: '' },
        { category: 'Administrative Costs', description: '', amount: '' },
        { category: 'Other', description: '', amount: '' },
      ],
      budgetFile: null,
      applicantType: '',
      individualAddress: '',
      entityEIN: '',
      entityDocs: null,
      revenueYearOne: 0,
      revenueYearTwo: 0,
      depositAgreement: false,
      previousFiscalSponsor: '',
      fundsToTransfer: '',
      transferAmount: '',
      heardAboutUs: '',
      referralSource: '',
      otherSource: '',
      linkedin: '',
      instagram: '',
      facebook: '',
      otherSocials: '',
      comments: ''
    });
  };

  const formatSocialMediaUrl = (platform, handle) => {
    // Remove @ symbol if included
    handle = handle.replace('@', '');
    
    switch (platform) {
      case 'twitter':
        return `https://twitter.com/${handle}`;
      case 'instagram':
        return `https://instagram.com/${handle}`;
      case 'linkedin':
        return `https://linkedin.com/in/${handle}`;
      default:
        return handle;
    }
  };

  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    // Extract just the handle/username portion
    const handle = value.split('/').pop().replace('@', '');
    
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [name]: formatSocialMediaUrl(name, handle)
      }
    }));
  };

  // Add a function to generate a secure reference number
  const generateSecureReference = (applicationId) => {
    // Create a reference number using timestamp and a random component
    const timestamp = new Date().getTime().toString(36);
    const random = Math.random().toString(36).substring(2, 7);
    const prefix = 'CI'; // Cause & Impact prefix
    
    // Combine elements but don't use actual applicationId
    return `${prefix}-${timestamp}-${random}`.toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started');
    
    try {
      setIsSubmitting(true);
      console.log('Form data being submitted:', formData);

      const response = await fetch('/api/applications/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error('Failed to submit application');
      }

      const data = await response.json();
      console.log('Success response:', data);

      // Generate secure reference instead of using database ID
      const secureReference = generateSecureReference(data.applicationId);
      setApplicationId(secureReference); // Store reference number instead of DB ID
      setShowSuccessModal(true);

      // Track form submission with secure reference
      try {
        window.analytics?.track('Form Submitted', {
          formName: 'Application Form',
          referenceNumber: secureReference // Use reference number in analytics
        });
      } catch (analyticsError) {
        console.warn('Analytics tracking failed:', analyticsError);
      }

      // Redirect after delay
      setTimeout(() => {
        try {
          window.location.href = '/academy';
        } catch (redirectError) {
          console.error('Redirect failed:', redirectError);
          // Fallback navigation
          window.location.replace('/academy');
        }
      }, 3000);

    } catch (error) {
      console.error('Form submission error:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnalyticsForm name="Application">
      <div className="application-wrapper">
        <form 
          className="application-form"
          onSubmit={handleSubmit}
        >
          <h2>Fiscal Sponsorship Application</h2>
          <p>Provide detailed information about your Cause to get started.</p>

          {/* Personal Information Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Personal Information</h3>
              <p>Tell us about yourself and how to contact you</p>
            </div>
            <div className="form-row form-row-2">
              <label>
                Your Name:
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Email Address:
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Phone Number:
                <input
                  className="input-medium"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  placeholder="(XXX) XXX-XXXX"
                  maxLength="14"
                  onChange={(e) => {
                    const formattedPhone = formatPhoneNumber(e.target.value);
                    setFormData({ ...formData, phone: formattedPhone });
                  }}
                  required
                />
              </label>
            </div>
          </div>

          {/* Cause Information Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Cause Details</h3>
              <p>Tell us about your Cause and its mission</p>
            </div>
            <div className="form-row form-row-2">
              <label>
                Cause Name:
                <input
                  type="text"
                  name="causeName"
                  value={formData.causeName}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Cause URL:
                <input
                  type="url"
                  name="causeURL"
                  value={formData.causeURL ? (formData.causeURL.startsWith('http') ? formData.causeURL : `https://${formData.causeURL}`) : ''}
                  onChange={(e) => {
                    let url = e.target.value;
                    // Remove any existing protocol
                    url = url.replace(/^https?:\/\//, '');
                    setFormData({
                      ...formData,
                      causeURL: url
                    });
                  }}
                  placeholder="e.g., www.example.com"
                />
              </label>
            </div>

            <label>
              Location:
              <div className="location-inputs">
                <input
                  type="text"
                  name="locationCity"
                  placeholder="City"
                  value={formData.locationCity}
                  onChange={handleChange}
                  required
                />
                <input
                  type="text"
                  name="locationState"
                  placeholder="State"
                  value={formData.locationState}
                  onChange={handleChange}
                  required
                />
              </div>
            </label>

            <label>
              Mission Statement:
              <textarea
                name="missionStatement"
                value={formData.missionStatement}
                maxLength="255"
                onChange={handleChange}
                required
                placeholder="Describe your mission in 255 characters or less"
              ></textarea>
            </label>

            <label>
              Vision, Cause, and Goals:
              <textarea
                name="visionGoals"
                value={formData.visionGoals}
                onChange={handleChange}
                required
                placeholder="Describe your long-term vision and goals"
              ></textarea>
            </label>
          </div>

          {/* Financial Information Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Financial Information</h3>
              <p>Tell us about your funding plans and budget</p>
            </div>

            {/* Revenue Projections */}
            <div className="revenue-slider-container">
              <div className="revenue-label">First-Year Revenue Projection</div>
              <div className="revenue-amount">{formatCurrency(formData.revenueYearOne)}</div>
              <div className="slider-track">
                <div 
                  className="slider-fill" 
                  style={{ width: `${(getSliderValue(formData.revenueYearOne) / 200) * 100}%` }}
                />
                <input
                  type="range"
                  name="revenueYearOne"
                  min="0"
                  max="200"
                  step="1"
                  value={getSliderValue(formData.revenueYearOne)}
                  onChange={(e) => handleSliderChange(e, 'revenueYearOne')}
                />
              </div>
              <div className="slider-ranges">
                {revenueSliderRanges.map((label, index) => (
                  <span key={index}>{label}</span>
                ))}
              </div>
            </div>

            <div className="revenue-slider-container">
              <div className="revenue-label">Second-Year Revenue Projection</div>
              <div className="revenue-amount">{formatCurrency(formData.revenueYearTwo)}</div>
              <div className="slider-track">
                <div 
                  className="slider-fill" 
                  style={{ width: `${(getSliderValue(formData.revenueYearTwo) / 200) * 100}%` }}
                />
                <input
                  type="range"
                  name="revenueYearTwo"
                  min="0"
                  max="200"
                  step="1"
                  value={getSliderValue(formData.revenueYearTwo)}
                  onChange={(e) => handleSliderChange(e, 'revenueYearTwo')}
                />
              </div>
              <div className="slider-ranges">
                {revenueSliderRanges.map((label, index) => (
                  <span key={index}>{label}</span>
                ))}
              </div>
            </div>

            {/* Budget Upload Section */}
            <div className="file-upload-section">
              <div className="file-upload-container">
                <label className="file-upload-label">
                  <div className="file-upload-icon">📄</div>
                  <span>Upload Formal Budget or Business Plan</span>
                  <p>Drag and drop your file here, or click to browse</p>
                  <input
                    type="file"
                    name="budgetFile"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xlsx"
                  />
                </label>
              </div>
              <div className="template-option">
                Or use our provided budget template
              </div>
            </div>

            {/* Budget Table */}
            <table className="budget-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Amount (USD)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { category: 'Programmatic Supplies', description: 'E.g., materials for events, tools, etc.' },
                  { category: 'Contractor Payments', description: 'Payments to 1099 contractors' },
                  { category: 'Vendor Payments', description: 'Payments to third-party vendors' },
                  { category: 'Scholarships', description: 'Regranting scholarships to beneficiaries' },
                  { category: 'Travel', description: 'Travel-related expenses (e.g., flights)' },
                  { category: 'Marketing', description: 'Marketing and outreach costs' },
                  { category: 'Administrative Costs', description: 'Office supplies, software, etc.' },
                  { category: 'Other', description: 'Any other expenses not listed' },
                ].map((row, index) => (
                  <tr key={index}>
                    <td>{row.category}</td>
                    <td>{row.description}</td>
                    <td>
                      <input
                        type="text"
                        placeholder="$0"
                        value={formatDollarAmount(formData.budgetDetails[index]?.amount)}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/[^0-9.]/g, '');
                          const updatedDetails = [...formData.budgetDetails];
                          updatedDetails[index] = {
                            category: row.category,
                            description: row.description,
                            amount: rawValue
                          };
                          setFormData({ ...formData, budgetDetails: updatedDetails });
                        }}
                        required
                      />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="2" style={{ textAlign: 'right' }}>
                    <strong>Total:</strong>
                  </td>
                  <td>
                    <strong>
                      {formatDollarAmount(
                        formData.budgetDetails
                          ?.reduce((sum, row) => sum + Number(row?.amount || 0), 0)
                      )}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Funding Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Funding & Grants</h3>
              <p>Tell us about your fundraising plans and grant opportunities</p>
            </div>
            
            <label>
              Are you fundraising? (Select all that apply):
              <div className="checkbox-group">
                {[
                  'Individual Donations',
                  'Corporate Donations',
                  'Personal Contributions',
                  'Private Grants',
                  'Government Grants',
                  'Online Fundraising',
                  'In-Person Fundraising Events',
                ].map((option) => (
                  <label key={option} className="checkbox-option">
                    <input
                      type="checkbox"
                      value={option}
                      checked={formData.fundraisingPlans.includes(option)}
                      onChange={() => toggleFundraisingOption(option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </label>

            {formData.fundraisingPlans.includes('Government Grants') && (
              <div className="form-subsection">
                <label>
                  Are you pursuing or exploring funding opportunities?
                  <select
                    name="grantInterest"
                    value={formData.grantInterest}
                    onChange={handleChange}
                  >
                    <option value="">Select one</option>
                    <option value="Interested in Pursuing">Interested in Pursuing</option>
                    <option value="Funding Already Lined Up">Funding Already Lined Up</option>
                  </select>
                </label>

                {formData.grantInterest === 'Funding Already Lined Up' && (
                  <>
                    <h4>Grant Funding Details</h4>
                    {formData.grantDetails.map((detail, index) => (
                      <div key={index} className="grant-detail-card">
                        <label>
                          Funder:
                          <input
                            type="text"
                            value={detail.funder}
                            onChange={(e) => handleGrantDetailsChange(index, 'funder', e.target.value)}
                          />
                        </label>
                        <label>
                          Amount:
                          <input
                            type="number"
                            value={detail.amount}
                            onChange={(e) => handleGrantDetailsChange(index, 'amount', e.target.value)}
                          />
                        </label>
                        <label>
                          Restrictions or Reporting Requirements:
                          <textarea
                            value={detail.restrictions}
                            onChange={(e) => handleGrantDetailsChange(index, 'restrictions', e.target.value)}
                          ></textarea>
                        </label>
                        <div className="file-upload-container">
                          <label className="file-upload-label">
                            <div className="file-upload-icon">📄</div>
                            <span>Upload RFP or Funding Documents</span>
                            <input
                              type="file"
                              onChange={(e) => handleGrantDetailsChange(index, 'rfp', e.target.files[0])}
                              accept=".pdf,.doc,.docx"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                    <button type="button" className="add-grant-button" onClick={addGrantDetail}>
                      Add Another Grant
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Organization Type Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Organization Details</h3>
              <p>Tell us about your organizational structure</p>
            </div>

            <label>
              Are you applying as an individual or entity?
              <select
                name="applicantType"
                value={formData.applicantType}
                onChange={handleChange}
                required
              >
                <option value="">Select one</option>
                <option value="Individual">Individual</option>
                <option value="Entity">Entity</option>
              </select>
            </label>

            {formData.applicantType === 'Individual' && (
              <div className="form-row">
                <label>
                  Address:
                  <input
                    type="text"
                    name="individualAddress"
                    value={formData.individualAddress}
                    onChange={handleChange}
                    required
                  />
                </label>
              </div>
            )}

            {formData.applicantType === 'Entity' && (
              <div className="form-row">
                <label>
                  EIN:
                  <input
                    type="text"
                    name="entityEIN"
                    value={formData.entityEIN}
                    placeholder="XX-XXXXXXX"
                    pattern="\d{2}-\d{7}"
                    onChange={handleChange}
                    required
                  />
                </label>
                <div className="file-upload-container">
                  <label className="file-upload-label">
                    <div className="file-upload-icon">📄</div>
                    <span>Upload Articles of Formation or Entity Documents</span>
                    <input
                      type="file"
                      name="entityDocs"
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx"
                      required
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Previous Fiscal Sponsorship Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Previous Fiscal Sponsorship</h3>
              <p>Tell us about any previous fiscal sponsorship arrangements</p>
            </div>

            <label>
              Have you been fiscally sponsored before?
              <select
                name="previousFiscalSponsor"
                value={formData.previousFiscalSponsor}
                onChange={handleChange}
                required
              >
                <option value="">Select one</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            {formData.previousFiscalSponsor === 'Yes' && (
              <>
                <label>
                  Do you have existing funds that need to be transferred?
                  <select
                    name="fundsToTransfer"
                    value={formData.fundsToTransfer}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select one</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </label>

                {formData.fundsToTransfer === 'Yes' && (
                  <label>
                    Amount to be transferred (USD):
                    <input
                      type="number"
                      name="transferAmount"
                      value={formData.transferAmount || ''}
                      onChange={handleChange}
                      placeholder="$0"
                      required
                    />
                  </label>
                )}
              </>
            )}
          </div>

          {/* Social Media Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Social Media Presence</h3>
              <p>Share your social media profiles (Optional)</p>
            </div>
            <div className="socials-group">
              <div className="social-input-container">
                <span className="social-icon linkedin"></span>
                <input
                  type="url"
                  name="linkedin"
                  value={formData.linkedin || ''}
                  onChange={handleChange}
                  placeholder="LinkedIn Profile URL"
                />
              </div>
              <div className="social-input-container">
                <span className="social-icon instagram"></span>
                <input
                  type="url"
                  name="instagram"
                  value={formData.instagram || ''}
                  onChange={handleChange}
                  placeholder="Instagram Profile URL"
                />
              </div>
              <div className="social-input-container">
                <span className="social-icon facebook"></span>
                <input
                  type="url"
                  name="facebook"
                  value={formData.facebook || ''}
                  onChange={handleChange}
                  placeholder="Facebook Profile URL"
                />
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Additional Comments</h3>
              <p>Share any additional information that might be relevant to your application</p>
            </div>
            <div className="comments-section">
              <label>
                Comments (Optional):
                <textarea
                  name="comments"
                  value={formData.comments || ''}
                  onChange={handleChange}
                  className="comments-textarea"
                  placeholder="Is there anything else you'd like us to know about your Cause or organization?"
                  maxLength={1000}
                ></textarea>
              </label>
              <div className={`character-count ${
                formData.comments?.length > 900 ? 'near-limit' : ''
              } ${formData.comments?.length > 950 ? 'at-limit' : ''}`}>
                {formData.comments?.length || 0}/1000 characters
              </div>
            </div>
          </div>

          {/* Submit Section */}
          <div className="form-section submit-section">
            <div className="submit-container">
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
                onClick={() => analytics.trackClick('Submit Button')}
              >
                <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
              </button>
            </div>
          </div>
        </form>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="success-modal-overlay">
            <div className="success-modal">
              <div className="success-icon">✨</div>
              <h2>Application Submitted!</h2>
              <p>Thank you for applying to be part of Cause & Impact.</p>
              <p>We'll review your application within 48-72 hours and get back to you soon.</p>
              <p className="application-id">Application ID: {applicationId}</p>
              <p className="redirect-message">
                Redirecting you to Impact Academy in 3 seconds...
              </p>
            </div>
          </div>
        )}
      </div>
    </AnalyticsForm>
  );
};

export default ApplicationForm;
